/**
 * Energy formatting utilities (stored or consumed energy)
 * For power (rate of energy), use power.ts
 * For electrical units (voltage, current), use voltage.ts and current.ts
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { EnergyFormat, EnergyUnit, FieldValue, FormattedValue } from "./types";

/**
 * Format as Watt-hours
 */
export const toWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} Wh`;
};

/**
 * Format as kilowatt-hours
 */
export const tokWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1000).toFixed(decimals)} kWh`;
};

/**
 * Format as megawatt-hours
 */
export const toMWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1_000_000).toFixed(decimals)} MWh`;
};

/**
 * Format as gigawatt-hours
 */
export const toGWh = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1_000_000_000).toFixed(decimals)} GWh`;
};

/**
 * Pick the display unit auto-scaling would choose for a Wh magnitude.
 */
export const pickEnergyUnit = (whValue: number): EnergyUnit => {
  const absValue = Math.abs(whValue);
  if (absValue >= 1_000_000_000) return "GWh";
  if (absValue >= 1_000_000) return "MWh";
  if (absValue >= 1000) return "kWh";
  return "Wh";
};

/**
 * Auto-scale energy to the most appropriate unit
 */
export const autoScaleEnergy = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;

  switch (pickEnergyUnit(value)) {
    case "GWh":
      return toGWh(value, decimals);
    case "MWh":
      return toMWh(value, decimals);
    case "kWh":
      return tokWh(value, decimals);
    default:
      return toWh(value, decimals);
  }
};

// Power formatting has been moved to power.ts

/**
 * Format energy according to EnergyFormat specification
 */
/**
 * Multiplier from the named energy unit to Wh — the formatter's internal
 * reference unit. Keep in sync with `EnergyUnit`.
 */
export const SOURCE_UNIT_TO_WH: Record<EnergyUnit, number> = {
  Wh: 1,
  kWh: 1_000,
  MWh: 1_000_000,
  GWh: 1_000_000_000,
};

export const formatEnergy = (value: FieldValue, format: EnergyFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  // Normalize the input to Wh so the unit-switch and auto-scale below can
  // assume their historical reference unit. Callers passing a value already
  // in Wh can omit `sourceUnit`.
  const whValue = format.sourceUnit ? numValue * SOURCE_UNIT_TO_WH[format.sourceUnit] : numValue;

  const decimals = format.decimals ?? 1;

  // If a specific unit is provided, use it
  if (format.unit) {
    switch (format.unit) {
      case "GWh":
        return toGWh(whValue, decimals);
      case "MWh":
        return toMWh(whValue, decimals);
      case "kWh":
        return tokWh(whValue, decimals);
      case "Wh":
        return toWh(whValue, decimals);
    }
  }

  // Default to auto-scaling unless explicitly disabled
  if (format.autoScale !== false) {
    return autoScaleEnergy(whValue, decimals);
  }

  // If auto-scale is explicitly false and no unit specified, use base Wh
  return toWh(whValue, decimals);
};

// Electrical units have been moved to voltage.ts and current.ts
// Resistance units (Ohms) could be moved to a separate resistance.ts if needed

// Register energy and power formatters
FormatRegistry.register("kWh", (value) => {
  if (typeof value === "number") return tokWh(value);
  return null;
});

// Power formatters are now in power.ts

FormatRegistry.register("autoScaleEnergy", (value, decimals) => {
  if (typeof value === "number") {
    return autoScaleEnergy(value, typeof decimals === "number" ? decimals : 1);
  }
  return null;
});

// Auto-scale power is now in power.ts

// Register the energy formatter
// biome-ignore lint/suspicious/noExplicitAny: Required for FormatRegistry type compatibility
FormatRegistry.register("energy", formatEnergy as any);
